import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { PractitionersApi, PractitionerSearchResponse } from '~/api';
import { getBaseUrl } from '~/env';
import { trackEvent } from '~/hooks';
import {
  boundsAreNotEqual,
  groupSearchResultsByLocation,
  Location,
  SearchResultGroupByLocation,
} from '~/utilities';
import { AvailabilityFilters } from '~/utilities/constants';
import { getDateRangeForAvailability } from '~/utilities/helpers';

function getDateRange(availability?: AvailabilityFilters) {
  if (
    availability === undefined ||
    availability === null ||
    availability === AvailabilityFilters.All
  ) {
    return undefined;
  }
  const dateRange = getDateRangeForAvailability(availability);
  if (dateRange?.startDateUtc && dateRange?.endDateUtc) {
    return {
      startDateUtc: dateRange.startDateUtc,
      endDateUtc: dateRange.endDateUtc,
    };
  }
  return undefined;
}

const TWO_MINUTES = 2 * 60 * 1000;

export interface UseSearchPractitionersParams {
  discipline?: string;
  location?: Location;
  availability: AvailabilityFilters;
  mapBounds?: google.maps.LatLngBoundsLiteral;
}

export function useSearchPractitioners(params: UseSearchPractitionersParams) {
  const { discipline, location, availability, mapBounds } = params;

  const previousDisciplineRef = useRef<string | undefined>(undefined);
  const previousLocationRef = useRef<Location | undefined>(undefined);
  const previousAvailabilityRef = useRef<AvailabilityFilters | undefined>(
    undefined
  );
  const previousMapBoundsRef = useRef<
    google.maps.LatLngBoundsLiteral | undefined
  >(undefined);
  const previousResultsRef = useRef<SearchResultGroupByLocation[]>([]);
  const previousPageCountRef = useRef(0);

  const queryResults = useInfiniteQuery<PractitionerSearchResponse>({
    initialPageParam: undefined,
    queryKey: ['practitioners', discipline, location, availability, mapBounds],
    queryFn: async (context) => {
      const pageParam = context.pageParam as string | undefined;
      if (!discipline || !mapBounds || !location?.coordinates) {
        throw new Error('Missing required search parameters');
      }

      trackEvent('mpl_new_search_started', {
        url: window?.location?.pathname,
        discipline: discipline,
        location: location.value,
      });

      const api = new PractitionersApi({ baseUrl: getBaseUrl() });
      const dateRange = getDateRange(availability);

      const response = await api.search({
        maxResults: 50,
        boundingBox: {
          top: mapBounds.north,
          right: mapBounds.east,
          bottom: mapBounds.south,
          left: mapBounds.west,
        },
        discipline,
        latitude: location.coordinates.lat,
        longitude: location.coordinates.lng,
        nextToken: pageParam,
        dateRange,
      });

      const data: PractitionerSearchResponse = await response.json();
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
    enabled: !!(discipline && location && mapBounds),
    staleTime: TWO_MINUTES, // 2 minutes
  });

  const results = useMemo(() => {
    const currentPageCount = queryResults.data?.pages.length || 0;
    const hasChanged =
      previousDisciplineRef.current !== discipline ||
      previousAvailabilityRef.current !== availability ||
      previousLocationRef.current?.value !== location?.value ||
      boundsAreNotEqual(previousMapBoundsRef.current, mapBounds);

    if (hasChanged) {
      previousPageCountRef.current = 0;
      previousResultsRef.current = [];
    }

    const isLoadMore =
      !hasChanged &&
      currentPageCount > previousPageCountRef.current &&
      currentPageCount > 1;

    // For fresh search: flatten all results
    // For pagination: only get the NEW page results
    const resultsToProcess =
      isLoadMore && queryResults.data?.pages
        ? queryResults.data.pages[currentPageCount - 1]?.results || []
        : queryResults.data?.pages.flatMap((page) => page.results) || [];

    const processedResults = groupSearchResultsByLocation(
      resultsToProcess,
      isLoadMore,
      isLoadMore ? previousResultsRef.current : undefined
    );

    // Update refs after processing
    previousAvailabilityRef.current = availability;
    previousDisciplineRef.current = discipline;
    previousLocationRef.current = location;
    previousMapBoundsRef.current = mapBounds;
    previousPageCountRef.current = currentPageCount;
    previousResultsRef.current = processedResults;

    return processedResults;
  }, [queryResults.data?.pages, discipline, availability, location, mapBounds]);

  return {
    results,
    fetchNextPage: queryResults.fetchNextPage,
    hasNextPage: queryResults.hasNextPage,
    isFetching: queryResults.isFetching,
    isError: queryResults.isError,
    error: queryResults.error,
    refetch: queryResults.refetch,
  };
}
